/**
 * RFC 4180 CSV serialization.
 *
 * Pure and DOM-free on purpose: the download side lives in `downloadFile.ts`,
 * so the escaping rules below can be unit-tested without a browser.
 */

/** Characters that make a spreadsheet treat a cell as a formula. */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** Characters that force a field to be quoted. */
const MUST_QUOTE = /[",\n\r]/;

/**
 * Render one value as a CSV field.
 *
 * Two separate hazards, in order. First, a cell beginning `=`, `+`, `-` or `@`
 * is executed as a formula by Excel and Sheets, so a name like `=cmd|...` is a
 * live attack on whoever opens the file; we defuse it with a leading
 * apostrophe. (That would mangle a negative number — no column we export is a
 * signed number, so it is safe here.) Second, the ordinary RFC 4180 quoting.
 */
export function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';

  const raw = String(value);
  const defused = FORMULA_PREFIXES.some((prefix) => raw.startsWith(prefix))
    ? `'${raw}`
    : raw;

  // Leading/trailing spaces are quoted too, so they survive a round trip.
  const needsQuotes = MUST_QUOTE.test(defused) || defused.trim() !== defused;

  return needsQuotes ? `"${defused.replace(/"/g, '""')}"` : defused;
}

/**
 * Join rows into a CSV document. The header, if there is one, is just `rows[0]`.
 *
 * CRLF line endings per RFC 4180 — Excel on Windows prefers them and every
 * other reader accepts them.
 */
export function toCsv(rows: readonly (readonly unknown[])[]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}
